import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';
import { listMerchantCheckouts, reviewRefund } from '../api/endpoints/refunds';
import { listCustomerSubscriptions } from '../api/endpoints/subscriptions';
import type { MerchantCheckout, MerchantRefund } from '../api/schemas';
import { usePermissions } from '../auth/usePermissions';
import { formatDateTime, formatMoneyCents } from '../lib/format';
import { parsePriceInput } from '../lib/money';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import { ConfirmAction } from './ConfirmAction';
import { ErrorBanner } from './ErrorBanner';
import { FormField } from './FormField';
import { LoadingState } from './LoadingState';
import { SubscriptionDetails } from './CustomerSubscriptions';

const QUERY_KEY = ['merchant-refund-checkouts'];
const STATUS: Record<MerchantRefund['status'], string> = {
  pending: 'Solicitud recibida', business_review: 'En revisión del negocio', approved: 'Monto aprobado',
  declined: 'Solicitud rechazada', submitted: 'Reembolso en proceso', refunded: 'Reembolso confirmado',
  failed: 'Reembolso pendiente de resolver',
};

export function RefundReview() {
  return usePermissions().can('anular') ? <OwnerRefunds /> : null;
}

function OwnerRefunds() {
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: listMerchantCheckouts, retry: false });
  const checkouts = query.data?.data.filter(checkout => checkout.refund) ?? [];
  return <section className="section" aria-label="Devoluciones">
    <h2 className="section-label">Devoluciones</h2>
    <p className="row-sub">Solicitudes de los cobros recientes. Aprobar un monto no confirma que el proveedor lo haya devuelto.</p>
    <button type="button" className="btn small secondary" disabled={query.isFetching} onClick={() => void query.refetch()}>
      Actualizar devoluciones
    </button>
    {query.isLoading ? <LoadingState label="Cargando devoluciones…" /> : null}
    {query.isError ? <ErrorBanner message={message(query.error, 'No pudimos cargar las devoluciones.')} /> : null}
    {query.isSuccess && !checkouts.length ? <p className="row-sub">No hay solicitudes en los cobros recientes.</p> : null}
    <ul className="rows">{checkouts.map(checkout => <li key={checkout.id}><RefundRow checkout={checkout} /></li>)}</ul>
  </section>;
}

function RefundRow({ checkout }: { checkout: MerchantCheckout }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string>();
  const [confirming, setConfirming] = useState<number>();
  const refund = checkout.refund;
  const editable = refund?.status === 'business_review' || refund?.status === 'pending';
  const needsUsage = editable && checkout.target.kind === 'pack' && checkout.fulfillmentStatus === 'fulfilled';
  const subscriptions = useQuery({ queryKey: ['customer-subscriptions', checkout.customerId],
    queryFn: () => listCustomerSubscriptions(checkout.customerId), enabled: needsUsage, retry: false });
  const pack = subscriptions.data?.data.find(item => item.id === checkout.fulfillmentId);
  const mutation = useMutationAttempt({
    mutationFn: (decision: { approvedAmountCents: number | null }, key: string) => reviewRefund(checkout.id, decision, key),
    onSuccess: updated => {
      setConfirming(undefined);
      queryClient.setQueryData<Awaited<ReturnType<typeof listMerchantCheckouts>>>(QUERY_KEY,
        current => current ? { ...current, data: current.data.map(item => item.id === updated.id ? updated : item) } : current);
    },
  });
  if (!refund) return null;
  const locked = mutation.isPending || mutation.uncertain || (needsUsage && !pack);

  function requestApproval() {
    const price = parsePriceInput(amount);
    if (!price.ok || price.cents <= 0 || price.cents > refund!.requestedAmountCents) {
      setError(`El monto debe ser mayor que cero y no superar ${formatMoneyCents(refund!.requestedAmountCents)}.`);
      return;
    }
    setError(undefined); setConfirming(price.cents);
  }

  return <div className="card form-grid">
    <div>
      <p className="row-title">{STATUS[refund.status]}</p>
      <p className="row-sub">{checkout.target.kind === 'pack' ? 'Paquete' : 'Reserva'} · {checkout.provider === 'yappy' ? 'Yappy' : 'PagueloFacil'} · {formatDateTime(checkout.createdAt)}</p>
      <p className="row-sub">Cobro: {checkout.id}</p>
      <p>Solicitado: {formatMoneyCents(refund.requestedAmountCents)}{refund.approvedAmountCents !== null ? ` · Aprobado: ${formatMoneyCents(refund.approvedAmountCents)}` : ''}</p>
      {refund.ownerTask && !editable ? <p className="row-sub">Resuelve esta devolución con tu proveedor. El pago devuelto aún no está confirmado en Dale.</p> : null}
    </div>
    {needsUsage && pack ? <SubscriptionDetails subscription={pack} /> : null}
    {needsUsage && !pack ? <div>
      {subscriptions.isLoading ? <LoadingState label="Consultando uso del paquete…" /> : <ErrorBanner message="No pudimos verificar el uso de este paquete." />}
      <button type="button" className="btn small secondary" disabled={subscriptions.isFetching} onClick={() => void subscriptions.refetch()}>Actualizar uso del paquete</button>
    </div> : null}
    {editable ? <>
      {checkout.target.kind === 'pack' ? <p className="row-sub">Al aprobar, Dale retira el paquete y sus créditos restantes.</p> : null}
      <FormField label="Monto a aprobar" htmlFor={`refund-${checkout.id}`} error={error} hint="En dólares. Revisa el uso del paquete antes de decidir.">
        <input id={`refund-${checkout.id}`} inputMode="decimal" value={amount} disabled={locked || confirming !== undefined}
          onChange={event => setAmount(event.target.value)} />
      </FormField>
      <div className="action-pair">
        {confirming === undefined ? <button className="btn small" type="button" disabled={locked} onClick={requestApproval}>Aprobar monto</button>
          : <><button className="btn small" type="button" disabled={locked} onClick={() => mutation.mutate({ approvedAmountCents: confirming })}>
            Confirmar {formatMoneyCents(confirming)}
          </button><button className="btn small ghost" type="button" disabled={locked} onClick={() => setConfirming(undefined)}>Volver</button></>}
        <ConfirmAction label="Rechazar solicitud" confirmLabel="Sí, rechazar" variant="danger" disabled={locked || confirming !== undefined}
          pending={mutation.isPending} onConfirm={() => mutation.mutate({ approvedAmountCents: null })} />
      </div>
    </> : null}
    {mutation.isError ? <ErrorBanner message={message(mutation.error, 'No pudimos confirmar la decisión. Reintenta para consultar el resultado.')} /> : null}
    {mutation.uncertain ? <button type="button" className="btn small secondary" disabled={mutation.isPending} onClick={mutation.retry}>Reintentar decisión</button> : null}
  </div>;
}

function message(error: unknown, fallback: string) { return error instanceof ApiError ? error.message : fallback; }
